// #region Imports

import type { CssColor } from './CssColor';

// #endregion

/**
 * Represents the roles available in a theme scheme.
 *
 * @public
 */
export type ThemeSchemeRole = | 'surface' | 'background' | 'foreground' | 'highlight' | 'middlelight' | 'lowlight' | 'transparent' | 'semiTransparent' | 'disabled' | 'contrast' | 'selection';

/**
 * Represents a semantic color scheme.
 *
 * ThemeSchemeRole → CssColor
 *
 * @public
 */
export type ThemeScheme = Readonly<Partial<Record<ThemeSchemeRole, CssColor>>>;

/**
 * @public
 */
export namespace ThemeScheme {

    const ROLES: ReadonlyArray<ThemeSchemeRole> = ['surface', 'background', 'foreground', 'highlight', 'middlelight', 'lowlight', 'transparent', 'semiTransparent', 'disabled', 'contrast', 'selection'];

    function isRole(value: string): value is ThemeSchemeRole {
        return ROLES.includes(value as ThemeSchemeRole);
    }

    /**
     * Checks whether a value is a ThemeScheme.
     */
    export function isThemeScheme(value: unknown): value is ThemeScheme {
        if (typeof value !== 'object' || value === null) {
            return false;
        }

        return Object.entries(value).every(
            ([key, color]) => isRole(key) && typeof color === 'string'
        );
    }
}
